import { baseSuccessToastMessage } from "@/primevue/init";
import { useToast } from "primevue/usetoast";
import { watch, computed, ref, watchEffect } from "vue";
import type { Ref } from "vue";
import { useRoute } from "vue-router";
import { defineStore, storeToRefs } from "pinia";
import {
    ArtefactMeta,
    Artefact,
    ArtefactGroupWithArtefacts,
    Contributor,
    HistoryEntry,
} from ".";
import { followLink } from "@/lib/openapi/helpers";
import { types, api } from "@/lib/openapi";
import { useCurrentEntityManager } from "@/stores/entity-store";
import {
    discardDraftsAfterSync,
    FILE_SYNC_THROTTLE_MS,
    useDraftManager,
} from "@/composables/draft-manager";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";
import { useEditManager } from "@/composables/edit-manager";
import { throttle } from "lodash";
import { EntityType } from "@/stores/entity-store";
import { syncChannel } from "@/lib/broadcast-channels";

type ArtefactGroupId = string;

export async function getArtefactsTree(
    artefactGroupId: ArtefactGroupId,
    signal?: AbortSignal
): Promise<Array<ArtefactMeta | ArtefactGroupWithArtefacts>> {
    const { data, response } =
        await api.startBizArtefactManagementGetArtefactsTreeStartPost({
            body: {
                "artefact_group_id": artefactGroupId,
            },
            ...signal,
        });
    if (response.status !== 200 || !data) {
        throw new Error("Failed to fetch artefacts tree");
    }

    return data;
}

export async function get(
    artefact: ArtefactMeta,
    revision?: string | undefined
): Promise<Artefact> {
    let file_: File;
    let resp: { data: unknown; response: Response };

    // Execute request based on `artefact.links`.
    if (!artefact.links || !Object.keys(artefact.links).length) {
        const requestData: types.StartBizArtefactManagementGetArtefactStartPostData["body"] =
            {
                "artefact_id": artefact.identity,
            };

        if (revision) {
            requestData["revision"] = revision;
        }

        resp = await api.startBizArtefactManagementGetArtefactStartPost({
            body: {
                artefact_id: artefact.identity,
                revision: revision,
            },
        });
    } else {
        const query = revision
            ? new URLSearchParams({
                  revision: revision,
              })
            : undefined;
        resp = await followLink<File | Blob | string>(
            artefact,
            "get",
            undefined,
            undefined,
            query
        );
    }

    // Convert request into Artefact.
    const { data, response } = resp;

    if (!response.ok || data === undefined) {
        throw new Error(
            `File not found for: ${artefact.identity}. Response status: ${response.statusText}.`
        );
    }

    if (data instanceof File) {
        file_ = data;
    } else {
        // Data is either a Blob or a string.
        file_ = new File([data as Blob], artefact.name, {
            type: response.headers.get("Content-Type") || undefined,
        });
    }

    return new Artefact({
        identity: artefact.identity,
        revision: revision,
        name: file_.name,
        mimetype: response.headers.get("Content-Type") || file_.type || "",
        content: await file_.text(),
        blob: file_,
        links: artefact.links,
    });
}

export type ContributorWithDate = Omit<Contributor, "date"> & {
    date: Date;
};
export type HistoryEntryWithDate = Omit<
    HistoryEntry,
    "author" | "committer"
> & {
    author: ContributorWithDate;
    committer: ContributorWithDate;
};

interface CreateOrUpdateArtefactOptions {
    commitMessage?: string | null;
    showToast?: boolean;
}
const defaultOptions: CreateOrUpdateArtefactOptions = {
    commitMessage: null,
    showToast: true,
};

export interface EventDetails {
    mimetype: string;
    name: string;
}

export const events = new EventTarget();

function isNotArtefactMetaGroup(
    artefact: ArtefactMeta | ArtefactGroupWithArtefacts
): artefact is ArtefactMeta {
    return !("artefacts" in artefact);
}

export const useArtefactsStore = defineStore("artefacts", () => {
    const toast = useToast();
    const { task, process, role, product, domain } = useCurrentEntityManager();
    const isFetching = ref(false);
    const controller = ref(new AbortController());
    const syncStore = useSyncStore();

    const artefacts = ref<ArtefactMeta[]>([]);

    const entity = computed(() => {
        return (
            task.value ||
            process.value ||
            role.value ||
            product.value ||
            domain.value
        );
    });

    const updateArtefacts = async () => {
        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!entity.value?.user_artefact_group_id) {
            return;
        }

        isFetching.value = true;
        try {
            const artefactsAndArtefactGroups = await getArtefactsTree(
                entity.value.user_artefact_group_id,
                controller.value.signal
            );
            // TODO: Introduce logic to deal with folders // nested artefact groups. - FE#373
            artefacts.value = (artefactsAndArtefactGroups || []).filter(
                isNotArtefactMetaGroup
            );
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
    };

    async function addArtefact(
        ownerId: ArtefactGroupId,
        file: File,
        opts?: CreateOrUpdateArtefactOptions
    ): Promise<ArtefactMeta> {
        const options = { ...defaultOptions, ...opts };
        const { commitMessage, showToast } = options;

        const body: types.Body_start_biz_artefact_management_add_artefact_start_post =
            {
                file_: file,
                owner_id: ownerId,
            };
        if (commitMessage) {
            body.commit_message = commitMessage;
        }
        const { response, data } =
            await api.startBizArtefactManagementAddArtefactStartPost({
                body,
            });

        if (!response.ok) {
            if (response.status === 400) {
                toast.add({
                    severity: "error",
                    summary: "Could not save file - file is invalid",
                });
            } else if (response.status === 413) {
                toast.add({
                    severity: "error",
                    summary: "Could not save file - file is too large",
                });
            } else {
                toast.add({
                    severity: "error",
                    summary: `Could not save file - unknown error ${response.status}`,
                });
            }
            throw response;
        }

        if (!data) {
            throw Error("No artefact created.");
        }

        // Update artefacts list
        await updateArtefacts();

        events.dispatchEvent(
            new CustomEvent<EventDetails>("created", {
                detail: {
                    mimetype: file.type,
                    name: file.name,
                },
            })
        );
        if (showToast) {
            toast.add({
                ...baseSuccessToastMessage,
                summary: "File created",
            });
        }

        return data;
    }

    watch(
        () => entity.value?.user_artefact_group_id,
        () => {
            updateArtefacts();
        },
        { immediate: true }
    );

    async function getHistory(
        artefact: ArtefactMeta
    ): Promise<HistoryEntryWithDate[]> {
        let history: HistoryEntry[] = [];
        if (!artefact.links || !Object.keys(artefact.links).length) {
            const { data } =
                await api.startBizArtefactManagementGetHistoryStartPost({
                    body: {
                        artefact_id: artefact.identity,
                    },
                });
            if (data && "history" in data) {
                history = data.history;
            }
        } else if (
            "links" in artefact &&
            Object.keys(artefact.links).length &&
            "history" in artefact.links
        ) {
            const { data } =
                await followLink<types.StartBizEntitiesEntityTypeEntityIdAttributeHistoryGetResponse>(
                    artefact,
                    "history"
                );
            if (data && "history" in data) {
                history = data.history as HistoryEntry[];
            }
        } else {
            throw new Error("No history available for this artefact");
        }

        return history.map((entry) => ({
            ...entry,
            author: { ...entry.author, date: new Date(entry.author.date) },
            committer: {
                ...entry.committer,
                date: new Date(entry.committer.date),
            },
        }));
    }

    async function removeArtefact(
        artefactId: string,
        commitMessage: string | null = null
    ): Promise<void> {
        const body: types.Body_start_biz_artefact_management_remove_artefact_start_post =
            {
                artefact_id: artefactId,
            };

        if (commitMessage) {
            body.commit_message = commitMessage;
        }

        await api.startBizArtefactManagementRemoveArtefactStartPost({ body });

        // Update artefacts list
        await updateArtefacts();
    }

    async function restoreArtefact(
        artefact: ArtefactMeta,
        commitMessage: string | null = null
    ) {
        if (!artefact.revision) {
            throw new Error("Set a revision before restoring an artefact");
        }

        let data;

        if (
            artefact?.links &&
            Object.keys(artefact.links).length &&
            "restore" in artefact.links
        ) {
            const response =
                await followLink<types.StartBizArtefactManagementRestoreHistoricalArtefactStartPostData>(
                    artefact,
                    "restore",
                    { revision: artefact.revision }
                );
            data = response.data;
        } else {
            const body: types.Body_start_biz_artefact_management_restore_historical_artefact_start_post =
                {
                    artefact_id: artefact.identity,
                    revision: artefact.revision,
                };

            if (commitMessage) {
                body.commit_message = commitMessage;
            }

            data =
                await api.startBizArtefactManagementRestoreHistoricalArtefactStartPost(
                    { body }
                );
        }

        // Update artefacts list
        await updateArtefacts();

        return data;
    }

    async function renameArtefact(
        artefactId: string,
        newName: string,
        commitMessage: string | null = null
    ): Promise<void> {
        const body: types.Body_start_biz_artefact_management_rename_artefact_start_post =
            {
                artefact_id: artefactId,
                name: newName,
            };

        if (commitMessage) {
            body.commit_message = commitMessage;
        }

        await api.startBizArtefactManagementRenameArtefactStartPost({ body });

        // Update artefacts list
        await updateArtefacts();
    }

    async function updateArtefactContent(
        artefact: ArtefactMeta,
        file: File,
        opts?: CreateOrUpdateArtefactOptions
    ): Promise<void> {
        const options = { ...defaultOptions, ...opts };
        const { commitMessage, showToast } = options;

        const body: types.Body_start_biz_artefact_management_update_artefact_content_start_post =
            {
                file_: file,
                artefact_id: artefact.identity,
            };
        if (commitMessage) {
            body.commit_message = commitMessage;
        }

        const { response } =
            await api.startBizArtefactManagementUpdateArtefactContentStartPost({
                body,
            });
        if (!response.ok) {
            if (response.status === 400) {
                if (artefact.mimetype === "application/bpmn+xml") {
                    toast.add({
                        severity: "error",
                        summary: "Could not save file - bpmn file is invalid",
                    });
                } else {
                    toast.add({
                        severity: "error",
                        summary: "Could not save file - file is invalid",
                    });
                }
            }
        }
        events.dispatchEvent(
            new CustomEvent<EventDetails>("changed", {
                detail: {
                    mimetype: artefact.mimetype,
                    name: artefact.name,
                },
            })
        );
        if (showToast) {
            toast.add({
                ...baseSuccessToastMessage,
                summary: "File updated",
            });
        }
    }

    // Listen to changes for DOCUMENT artefacts.
    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities: SyncReturn) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) =>
                            identity.attribute === "name" &&
                            identity.entityType === EntityType.DOCUMENT
                    )
                ) {
                    updateArtefacts();
                }
            });
        }
    });

    return {
        artefacts,
        isFetching,
        updateArtefacts,
        addArtefact,
        updateArtefactContent,
        removeArtefact,
        renameArtefact,
        restoreArtefact,
        getHistory,
    };
});

export const useArtefactStore = defineStore("artefact", () => {
    const route = useRoute();
    const { artefacts } = useArtefactsManager();

    const artefact = computed<ArtefactMeta | undefined>(() => {
        if (!route) {
            return;
        }

        const { artefactId } = route.params;
        if (!artefactId) {
            return;
        }

        // No need to check if the artefact exists in the list of artefacts.
        // Better to do the fetch and use the response to determine if the
        // artefact exists. - mystekz#159
        return artefacts.value.find(
            (artefact: ArtefactMeta) => artefact.identity === artefactId
        );
    });

    // When the selected artefact changes, make sure to forget the selected
    // revision for the previous artefact, so that we don't accidentally
    // display a wrong revision when a previously selected artefact is reselected.
    watch(artefact, (_, oldArtefact) => {
        if (oldArtefact) {
            delete oldArtefact.revision;
        }
    });

    return {
        get,
        artefacts,
        artefact,
        events,
    };
});

export const useArtefactManager = () => storeToRefs(useArtefactStore());
export const useArtefactsManager = () => storeToRefs(useArtefactsStore());

export const withContent = (artefactMeta: Ref<ArtefactMeta | undefined>) => {
    const artefact = ref<Artefact>();
    const isFetching = ref(false);
    const syncStore = useSyncStore();

    watchEffect(async () => {
        if (!artefactMeta.value) {
            artefact.value = undefined;
        }

        if (artefactMeta.value) {
            isFetching.value = true;
            artefact.value = await get(
                artefactMeta.value,
                artefactMeta.value.revision
            );
            isFetching.value = false;
        }
    });

    // When revision of the artefact changes, also update the revision of
    // the meta artefact, so that we indirectly fetch the artefact associated
    // with the new revision.
    watch(
        () => artefact.value?.revision,
        (revision) => {
            if (artefactMeta.value) {
                artefactMeta.value.revision = revision;
            }
        }
    );

    const queueSave = async () => {
        if (!artefact.value?.isModified && !artefact.value?.isNameModified) {
            return;
        }

        // Avoid saving when we're viewing an older version.
        if (artefactMeta.value?.revision) {
            return;
        }

        const updateContent: types.StartBizArtefactManagementUpdateArtefactStartPostData =
            {
                body: {
                    artefact_id: artefact.value.identity,
                },
            };

        if (artefact.value?.isNameModified) {
            updateContent.body.name = artefact.value.name;
        }

        if (artefact.value?.isModified) {
            updateContent.body.content = artefact.value.blob;
        }

        syncStore.queueSync({
            entityType: EntityType.DOCUMENT,
            args: [updateContent],
        });

        artefact.value.persist();
    };

    // Here we could just check write actions and queue them
    const entityId = computed(() => artefactMeta.value?.identity);
    const artefactDraft = useDraftManager({
        entityId,
        entityType: EntityType.DOCUMENT,
        attribute: "file_",
        artefact,
    });
    discardDraftsAfterSync(entityId, [artefactDraft], EntityType.DOCUMENT);
    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);

    watch(
        [
            isEditing,
            () => artefact.value?.isModified,
            () => artefact.value?.isNameModified,
        ],
        ([_, modified, nameModified]) => {
            if (modified || nameModified) {
                throttledSave();
            }
        }
    );

    syncChannel.addEventListener(
        "message",
        async ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.entityType === EntityType.DOCUMENT &&
                identity.entityId === artefact.value?.identity
            ) {
                isFetching.value = true;
                artefact.value = await get(artefact.value!);
                isFetching.value = false;
            }
        }
    );

    return { artefact, isFetching };
};
